import { isWarehouseWhoWereReachingSnapshot } from "@act/shared/data-transfer-objects";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, LabelList } from "recharts";
import SectionReveal from "./SectionReveal";
import SectionBanner from "./SectionBanner";
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { ReportMockFieldFrame } from '../ReportMockFieldFrame';
import { foreground, muted, border, body } from "./tokens";
import { FONT_BODY, FONT_DISPLAY, TEXT_PRIMARY, TEXT_SECONDARY } from "./BrandChart";
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import { readHarpEnrollmentChartData } from './programGoalsChartData.util';

/** Studio legend copy for each HARP status — static definitions, not warehouse fields. */
const HARP_STATUS_DESCRIPTIONS: Record<string, string> = {
  Undecided:
    "Member has been spoken to about HARP but has not agreed or refused.",
  "Enrolled in HARP":
    "Current or formerly In Program member has confirmed enrollment in HARP.",
  Refused: "Member refused to enroll in a HARP.",
  "In Progress": "In Program member has agreed but enrollment is not complete.",
};

const PALETTE = [
  "var(--reema-coral-100)",
  "var(--reema-midnight-100)",
  "#0A3356",
  "#154A74",
];

const HARP_STATUS_LEGEND = [
  { label: "Undecided", description: HARP_STATUS_DESCRIPTIONS.Undecided },
  {
    label: "Enrolled in HARP",
    description: HARP_STATUS_DESCRIPTIONS["Enrolled in HARP"],
  },
  { label: "Refused", description: HARP_STATUS_DESCRIPTIONS.Refused },
  {
    label: "In Progress",
    description: HARP_STATUS_DESCRIPTIONS["In Progress"],
  },
] as const;

const HarpEnrollment = ({ data }: ReportHostedSectionProps) => {
  const chartRows = readHarpEnrollmentChartData(data);
  const chartSource = isWarehouseWhoWereReachingSnapshot(data)
    ? "warehouse"
    : "mock";
  const rows = chartRows.map((row, index) => ({
    ...row,
    color: PALETTE[index] ?? PALETTE[PALETTE.length - 1],
  }));

  return (
    <HostedReportSectionLayout
    sectionDomId="harp"
    className="section--white report-hosted-section-body"
    style={{ background: "var(--white)", fontFamily: body }}
    banner={
      <SectionBanner title="HARP Enrollment" iconHref="#icon_relationship_engine" />
    }
  >

      <SectionReveal delay={0.1}>
        <ReportMockFieldFrame data={data} field="statuses" source={chartSource}>
        <div
          className="rounded-2xl p-6 md:p-8 mb-10"
          style={{ background: "#fff", border: `1px solid ${border}` }}
        >
          <div className="harp-enrollment-content-grid grid md:grid-cols-2 gap-8 items-center">
            <div className="harp-enrollment-chart" style={{ width: "100%", height: 320 }}>
              {rows.length === 0 ? (
                <p className="text-sm mb-0" style={{ color: muted }}>
                  No HARP enrollment data for this reporting period yet. Use
                  Refresh data in the builder after warehouse program-goal rows
                  are available.
                </p>
              ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{ top: 24, right: 8, bottom: 24, left: 8 }}>
                  <YAxis type="number" hide />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 500, fill: TEXT_SECONDARY }}
                    interval={0}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={32}>
                    {rows.map((r) => (
                      <Cell key={r.label} fill={r.color} />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="top"
                      style={{
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 500,
                        fontSize: 14,
                        fill: TEXT_PRIMARY,
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              )}
            </div>

            <div className="harp-enrollment-legend space-y-4">
              {HARP_STATUS_LEGEND.map((s) => (
                <div key={s.label}>
                  <p style={{ fontFamily: body, fontSize: 14, color: foreground, margin: 0 }}>
                    <strong style={{ fontWeight: 700 }}>{s.label}:</strong>{" "}
                    <span style={{ color: muted }}>{s.description}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
        </ReportMockFieldFrame>
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default HarpEnrollment;
